import { Injectable } from '@jkhy/frontend-utils/di';
import { ObservationSource } from '@jkhy/frontend-utils/observable';
import { parseTime } from '@treasury/alarm-clock';
import { CompanyAccountsClient } from '@treasury/api/channel';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { removeAuthToken } from '@treasury/core/http';
import { LoggingService } from '@treasury/core/logging';
import { SessionStorageService } from '@treasury/utils';
import { AccountRequests } from '../../backoffice/requests';
import { BoAccountService } from '../../backoffice/services/account';
import { AccountService as ChannelAccountService } from '../../channel/services';
import { EntitlementsService } from '../../entitlements';
import { UsersService } from '../../users';
import { AuthenticationService } from '../authentication';
import { FeatureFlagService } from '../feature-flags/feature-flag.service';
import { IdleSignoutService } from './idle-signout-service';
import { LogoutReason } from './logout.types';
import { TimeAccessLogout } from './time-access-logout';

@Injectable()
export class LogoutManager {
    constructor(
        private readonly config: ConfigurationService,
        private readonly authService: AuthenticationService,
        private readonly channelAccountService: ChannelAccountService,
        private readonly boAccountService: BoAccountService,
        private readonly usersService: UsersService,
        private readonly idleSignoutService: IdleSignoutService,
        private readonly timeAccessLogout: TimeAccessLogout,
        private readonly companyAccountsClient: CompanyAccountsClient,
        private readonly sessionStorageService: SessionStorageService,
        private readonly entitlementsService: EntitlementsService,
        private readonly featureFlagService: FeatureFlagService,
        private readonly loggingService: LoggingService
    ) {
        this.idleSignoutService.sessionEnd$.subscribe(reason => this.logOut(reason));
    }

    private logoutSource = new ObservationSource<LogoutReason>();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public logout$ = this.logoutSource.toObservable();

    private idleDuration?: number;

    private loggingOut = false;

    /**
     * Starts or restarts the current logout timers.
     * Does nothing if a logout is already in progress.
     */
    public async startLogoutTimers() {
        if (this.loggingOut) {
            return;
        }
        this.startIdleSignout();
        this.startTimeAccessExpiredSignout();
    }

    public async logOut(reason: LogoutReason) {
        let result;
        if (this.loggingOut) {
            return undefined;
        }

        try {
            this.loggingOut = true;
            this.timeAccessLogout.stop();
            this.entitlementsService.reset();
            this.featureFlagService.reset();
            this.authService.invalidate();
            const logoutUrl = (await this.performLogoutRequest(reason)) || '';
            result = { link: logoutUrl };
        } catch (error) {
            this.loggingService.logError(
                'Error during logout',
                error instanceof Error ? error : new Error(String(error))
            );
        } finally {
            this.sessionStorageService.clear();
            removeAuthToken();
            this.logoutSource.emit(reason);
            this.loggingOut = false;
        }

        return result;
    }

    private async startIdleSignout() {
        if (this.loggingOut) {
            return;
        }

        if (!this.idleDuration) {
            const { idleTimeoutDuration } = await this.getUserSettings();
            this.idleDuration = idleTimeoutDuration || 0;
        }

        this.idleSignoutService.start(this.idleDuration);
    }

    private async getCurrentFiTime() {
        const authenticated = await this.authService.authenticated$.toPromise();
        if (!authenticated) {
            throw new Error('unable to obtain FI time before logging in');
        } else {
            const time =
                this.config.app === AppType.BackOffice
                    ? await AccountRequests.getCurrentFiDateTime()
                    : await this.companyAccountsClient
                          .companyAccountsGetCutoffTimes({ maxAgeInSeconds: 600 })
                          .then(response => response.data.currentFiTime);
            return parseTime(time);
        }
    }

    private async startTimeAccessExpiredSignout() {
        if (this.loggingOut) {
            return;
        }

        const fiCurrentTime = await this.getCurrentFiTime();

        const timeAccessSettings = await this.usersService.getCurrentUserTimeAccess();

        this.timeAccessLogout.startLogoutTimer(fiCurrentTime, timeAccessSettings, reason =>
            this.logOut(reason)
        );
    }

    private getUserSettings() {
        // user settings DTOs are consistent between services
        return this.config.app === AppType.BackOffice
            ? this.boAccountService.getUserSettings()
            : this.channelAccountService.getUserSettings();
    }

    private performLogoutRequest(reason: LogoutReason) {
        return this.config.app === AppType.BackOffice
            ? this.boAccountService.logout(reason)
            : this.channelAccountService.logout(reason);
    }
}
